import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  Play,
  Loader2,
  AlertCircle,
  XCircle,
  Info,
  RefreshCw,
  Monitor,
  Tablet,
  Smartphone
} from 'lucide-react'
import { scanUrl, getScanResult } from '../services/api'
import ScanResults from '../components/ScanResults'
import LoadingAnimation from '../components/LoadingAnimation'
import { normalizeUrl } from '../utils/urlNormalizer'

const scanSchema = z.object({
  url: z.string().min(1, 'Please enter a URL'),
  wcagLevel: z.enum(['A', 'AA', 'AAA']),
  includeWarnings: z.boolean(),
  viewport: z.object({
    width: z.number().min(320).max(3840),
    height: z.number().min(240).max(2160),
  }),
})

type ScanFormData = z.infer<typeof scanSchema>

// Viewport presets
const VIEWPORT_PRESETS = {
  desktop: { width: 1920, height: 1080, label: 'Desktop', icon: Monitor },
  tablet: { width: 768, height: 1024, label: 'Tablet', icon: Tablet },
  mobile: { width: 390, height: 844, label: 'Mobile', icon: Smartphone },
}

export default function ScanPage() {
  const [scanId, setScanId] = useState<string | null>(null)
  const [showResults, setShowResults] = useState(false)
  const [normalizedUrl, setNormalizedUrl] = useState<string>('')

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    // getValues,
  } = useForm<ScanFormData>({
    resolver: zodResolver(scanSchema),
    defaultValues: {
      url: '',
      wcagLevel: 'AA',
      includeWarnings: true,
      viewport: {
        width: 1920,
        height: 1080,
      },
    },
  })

  const scanMutation = useMutation({
    mutationFn: scanUrl,
    onSuccess: (data) => {
      setScanId(data.id)
      toast.success('Scan started successfully')
    },
    onError: (error: any) => {
      console.error('Scan error:', error)
      toast.error(error.response?.data?.error?.message || error.message || 'Failed to start scan')
      // Clear any hanging state
      setScanId(null)
      setShowResults(false)
    },
    retry: false,
  })

  const { data: scanResult, isLoading: isPolling } = useQuery({
    queryKey: ['scan', scanId],
    queryFn: () => getScanResult(scanId!),
    enabled: !!scanId && !showResults,
    refetchInterval: (data, query) => {
      if (data?.status === 'completed' || data?.status === 'failed') {
        setShowResults(true)
        return false
      }
      
      // Check timeout: if query has been running for more than 5 minutes
      const queryStartTime = query.state.dataUpdatedAt || Date.now()
      const elapsedTime = Date.now() - queryStartTime

      if (elapsedTime > 300000) { // 5 minutes timeout (increased from 90 seconds)
        console.log('Query timeout detected, stopping polling')
        toast.error('Test timed out after 5 minutes. This can happen with very complex websites. Please try again.')
        setScanId(null)
        setShowResults(false)
        return false
      }
      
      return 2000
    },
    retry: (failureCount, error: any) => {
      if (failureCount >= 3) return false
      
      // Don't retry on 404 errors (scan not found)
      if (error?.response?.status === 404) {
        toast.error('Test not found. Please try starting a new test.')
        setScanId(null)
        setShowResults(false)
        return false
      }
      
      return true
    },
    onError: (error: any) => {
      console.error('Polling error:', error)
      toast.error('Error while checking test status. Please try again.')
      setScanId(null)
      setShowResults(false)
    }
  })

  const onSubmit = (data: ScanFormData) => {
    // Normalize the URL before submitting
    const normalized = normalizeUrl(data.url)
    const submitData = { ...data, url: normalized }
    
    setShowResults(false)
    setScanId(null)
    scanMutation.mutate(submitData)
  }

  // Update normalized URL preview when user types
  const urlValue = watch('url')
  useEffect(() => {
    if (urlValue) {
      setNormalizedUrl(normalizeUrl(urlValue))
    } else {
      setNormalizedUrl('')
    }
  }, [urlValue])

  // Set viewport preset
  const setViewportPreset = (preset: keyof typeof VIEWPORT_PRESETS) => {
    const { width, height } = VIEWPORT_PRESETS[preset]
    setValue('viewport.width', width)
    setValue('viewport.height', height)
  }

  const isScanning = scanMutation.isLoading || isPolling
  
  // Debug logging
  useEffect(() => {
    console.log('ScanPage Debug:', {
      isScanning,
      isLoading: scanMutation.isLoading,
      isPolling,
      scanId,
      showResults
    })
  }, [isScanning, scanMutation.isLoading, isPolling, scanId, showResults])

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Single Page Accessibility Test
        </h1>
        <p className="text-gray-600">
          Test any webpage for WCAG compliance and get detailed accessibility insights
        </p>
      </div>

      {/* Scan Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="space-y-6">
          {/* URL Input */}
          <div>
            <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-2">
              Page URL
            </label>
            <input
              {...register('url')}
              type="text"
              placeholder="e.g. heise.de or https://example.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={scanMutation.isLoading}
            />
            {normalizedUrl && urlValue && normalizedUrl !== urlValue && (
              <p className="mt-2 text-sm text-gray-600 flex items-center">
                <Info className="w-4 h-4 mr-1" />
                Will test: <span className="font-mono ml-1">{normalizedUrl}</span>
              </p>
            )}
            {errors.url && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.url.message}
              </p>
            )}
          </div>

          {/* WCAG Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              WCAG Compliance Level
            </label>
            <div className="grid grid-cols-3 gap-3">
              {(['A', 'AA', 'AAA'] as const).map((level) => (
                <label
                  key={level}
                  className={`
                    relative flex items-center justify-center p-3 border rounded-lg cursor-pointer transition-colors
                    ${watch('wcagLevel') === level
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 hover:bg-gray-50'
                    }
                  `}
                >
                  <input
                    {...register('wcagLevel')}
                    type="radio"
                    value={level}
                    className="sr-only"
                    disabled={scanMutation.isLoading}
                  />
                  <span className="font-medium">Level {level}</span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Level AA is recommended for most websites
            </p>
          </div>

          {/* Viewport Settings */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Viewport Size
            </label>
            
            {/* Viewport Presets */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              {Object.entries(VIEWPORT_PRESETS).map(([key, preset]) => {
                const Icon = preset.icon
                const isActive = watch('viewport.width') === preset.width && 
                                watch('viewport.height') === preset.height
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setViewportPreset(key as keyof typeof VIEWPORT_PRESETS)}
                    disabled={scanMutation.isLoading}
                    className={`
                      flex items-center justify-center gap-2 px-3 py-2 border rounded-lg transition-colors
                      ${isActive
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:bg-gray-50'
                      }
                      ${scanMutation.isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{preset.label}</span>
                  </button>
                )
              })}
            </div>

            {/* Custom Size Inputs */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="width" className="block text-xs text-gray-500 mb-1">
                  Width (px)
                </label>
                <input
                  {...register('viewport.width', { valueAsNumber: true })}
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  disabled={scanMutation.isLoading}
                />
              </div>
              <div>
                <label htmlFor="height" className="block text-xs text-gray-500 mb-1">
                  Height (px)
                </label>
                <input
                  {...register('viewport.height', { valueAsNumber: true })}
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  disabled={scanMutation.isLoading}
                />
              </div>
            </div>
          </div>

          {/* Include Warnings */}
          <div className="flex items-center">
            <input
              {...register('includeWarnings')}
              type="checkbox"
              id="includeWarnings"
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              disabled={scanMutation.isLoading}
            />
            <label htmlFor="includeWarnings" className="ml-2 text-sm text-gray-700">
              Include warnings in the report (not just errors)
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={scanMutation.isLoading}
            className={`
              w-full flex items-center justify-center px-6 py-3 rounded-lg font-medium transition-colors
              ${scanMutation.isLoading
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
              }
            `}
          >
            {scanMutation.isLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Starting Scan...
              </>
            ) : (
              <>
                <Play className="w-5 h-5 mr-2" />
                Start Accessibility Test
              </>
            )}
          </button>
        </div>
      </form>

      {/* Scan Status */}
      {scanResult && !showResults && (
        <LoadingAnimation url={normalizedUrl || urlValue} />
      )}

      {/* Results */}
      {showResults && scanResult && scanResult.status === 'completed' && (
        <ScanResults scanId={scanId!} />
      )}

      {/* Error State */}
      {showResults && scanResult && scanResult.status === 'failed' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start">
            <XCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-red-900">
                Scan Failed
              </h3>
              <p className="text-sm text-red-700 mt-1">
                The scan could not be completed. Please check the URL and try again.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 inline-flex items-center text-sm font-medium text-red-700 hover:text-red-800"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
